import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Chip, { ChipSize } from "@galaxy-io/dls/chips/Chip";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Skeleton, { SkeletonVariant } from "@galaxy-io/dls/feedback/Skeleton";
import CopyInput from "@galaxy-io/dls/inputs/CopyInput";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import { FOCUS_RING } from "@galaxy-io/dls/styles/mixins";
import Text, { TextSize } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Tooltip from "@galaxy-io/dls/tooltip/Tooltip";
import Widget, { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import { workflowName } from "@gtm/contracts/workflows/catalog";
import { styled } from "@linaria/react";
import {
  ArrowClockwiseIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  DotsSixVerticalIcon,
  GithubLogoIcon,
  LightningIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { MenuItem, OverflowMenu } from "@/components/workspace/overflow-menu";
import { Status } from "@/components/workspace/status";
import { PersonEnrichmentDialog } from "@/features/workflows/person-enrichment-dialog";
import { apiClient, unwrap } from "@/lib/api-client";

const EmailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[8]};
`;
const EmailActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  flex-shrink: 0;
`;
const RunProgress = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[4]};
  min-width: 0;
  overflow-wrap: anywhere;
`;
const EmailList = styled.ul`
  padding: ${t.space[0]};
  list-style: none;
`;
const EmailLine = styled.li`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  padding: ${t.space[4]} ${t.space[0]};
  min-width: 0;
  border-top: 2px solid transparent;
  border-bottom: 2px solid transparent;
  &[data-drop="before"] { border-top-color: ${t.color.border.primary}; }
  &[data-drop="after"] { border-bottom-color: ${t.color.border.primary}; }
  & > * { flex-shrink: 0; }
  & > [data-email-value] { min-width: 0; flex: 1; }
`;
const DragHandle = styled.button`
  ${FOCUS_RING}
  width: 24px;
  height: 24px;
  border-radius: ${t.radius.md};
  padding: ${t.space[0]};
  border: 0;
  background: transparent;
  color: inherit;
  cursor: grab;
  &:active { cursor: grabbing; }
`;
const EmailEditor = styled.form`
  display: flex;
  align-items: center;
  gap: ${t.space[4]};
  min-width: 0;
  input { min-width: 0; width: 100%; }
  button { flex-shrink: 0; }
`;
export function PersonEmails({ id, username }: { id: string; username?: string }) {
  const client = useQueryClient();
  const router = useRouter();
  const [dragged, setDragged] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ email: string; after: boolean } | null>(null);
  const [enrich, setEnrich] = useState(false);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const run = useQuery({
    queryKey: ["person-enrichment-run", workflowId],
    enabled: !!workflowId,
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/workflows/run", {
          params: { query: { ref: workflowId! } },
          signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
        }),
      ),
    retry: false,
    refetchInterval: (query) =>
      query.state.status === "error" ||
      (query.state.data && !["running", "continued_as_new"].includes(query.state.data.status))
        ? false
        : 5000,
  });
  const activeRuns = useQuery({
    queryKey: ["person-email-runs", id],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/people/{id}/email-runs", {
          params: { path: { id } },
          signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
        }),
      ),
    retry: false,
    refetchInterval: (query) =>
      query.state.status !== "error" &&
      query.state.data?.some((active) => active.status === "running")
        ? 5000
        : false,
  });
  const currentRuns = (activeRuns.data ?? []).map((active) => ({
    workflowId: active.workflowId,
    name: workflowName(active.workflowKey),
    status: active.status as string,
    closedAt: active.closedAt,
    emailFound: active.progress?.emailFound,
  }));
  if (workflowId) {
    // Direct run status is fresher than the eventually consistent history list.
    const index = currentRuns.findIndex((active) => active.workflowId === workflowId);
    const previous = index >= 0 ? currentRuns.splice(index, 1)[0] : undefined;
    currentRuns.push({
      workflowId,
      name: "GitHub Email Enrich",
      closedAt: run.data?.closedAt ?? null,
      emailFound: previous?.emailFound,
      status: run.error
        ? "unknown"
        : run.data?.pendingActivities.some((activity) => activity.attempt > 1)
          ? "retrying"
          : (run.data?.status ?? "pending"),
    });
  }
  const running = currentRuns.some(
    (active) =>
      ["running", "retrying", "continued_as_new"].includes(active.status) &&
      (active.workflowId === workflowId ? !run.error : !activeRuns.error),
  );
  const openRuns = currentRuns.filter((active) => !active.closedAt);
  const visibleRuns = openRuns.length
    ? openRuns
    : currentRuns
        .filter((active) => active.closedAt)
        .sort((a, b) => b.closedAt!.localeCompare(a.closedAt!))
        .slice(0, 1);
  const emails = useQuery({
    queryKey: ["person-emails", id],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/people/{id}/emails", {
          params: { path: { id } },
          signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
        }),
      ),
    retry: false,
    // Enrichment and manual edits can finish outside this drawer. Keep the
    // addresses fresh independently of Temporal's eventually consistent status.
    refetchInterval: (query) => (query.state.status === "error" ? false : 5000),
  });
  const refreshing = emails.isFetching;
  // The run widget owns enrichment progress; only unresolved email reads need
  // a separate loading indicator when no active run already explains the wait.
  const loadingEmails = emails.isPending && !running;
  function refresh() {
    void emails.refetch();
    if (!activeRuns.isFetching) void activeRuns.refetch();
    if (workflowId && !run.isFetching) void run.refetch();
  }
  const save = useMutation({
    mutationFn: ({ value, primary }: { value: string; primary: boolean }) =>
      primary
        ? unwrap(
            apiClient().PUT("/people/{id}/emails/primary", {
              params: { path: { id } },
              body: { email: value },
            }),
          )
        : unwrap(
            apiClient().POST("/people/{id}/emails", {
              params: { path: { id } },
              body: { email: value },
            }),
          ),
    onSuccess: () => {
      setEmail("");
      setAdding(false);
      void client.invalidateQueries();
    },
  });
  const editList = useMutation({
    mutationFn: (change: { remove: string } | { order: string[] }) =>
      "remove" in change
        ? unwrap(
            apiClient().DELETE("/people/{id}/emails", {
              params: { path: { id } },
              body: { email: change.remove },
            }),
          )
        : unwrap(
            apiClient().PUT("/people/{id}/emails/order", {
              params: { path: { id } },
              body: { emails: change.order },
            }),
          ),
    onSuccess: (data) => {
      client.setQueryData(["person-emails", id], data);
      void client.invalidateQueries();
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: ["person-emails", id] });
    },
  });
  const addresses = [emails.data?.primaryEmail, ...(emails.data?.additionalEmails ?? [])].filter(
    (value): value is string => !!value,
  );
  const busy = save.isPending || editList.isPending;
  function move(value: string, index: number) {
    const ordered = addresses.filter((address) => address !== value);
    ordered.splice(index, 0, value);
    if (ordered.join() !== addresses.join()) editList.mutate({ order: ordered });
  }
  return (
    <>
      <ConfirmAction
        isOpen={!!removingEmail}
        onOpenChange={(open) => {
          if (!open) setRemovingEmail(null);
        }}
        header="Remove email?"
        description={`${removingEmail ?? "This email"} will be removed from this person’s contact list.`}
        label="Remove email"
        isDestructive
        onConfirm={async () => {
          if (removingEmail) await editList.mutateAsync({ remove: removingEmail });
        }}
      />
      <Widget
        header="Emails"
        actions={
          <EmailActions>
            <Button
              ariaLabel="Refresh emails and enrichment status"
              icon={ArrowClockwiseIcon}
              variant={ButtonVariant.TERTIARY}
              isLoading={refreshing}
              isDisabled={refreshing}
              onClick={refresh}
              tooltip="Refresh emails and enrichment status"
              size={ButtonSize.SMALL}
            />
            <Button
              ariaLabel="Add email"
              icon={PlusIcon}
              variant={ButtonVariant.TERTIARY}
              isDisabled={adding || busy}
              onClick={() => setAdding(true)}
              tooltip="Add email"
              size={ButtonSize.SMALL}
            />
            <OverflowMenu
              label="Enrich email"
              icon={LightningIcon}
              variant={ButtonVariant.TERTIARY}
            >
              {(close) => (
                <MenuItem
                  icon={GithubLogoIcon}
                  label="GitHub Email Enrich"
                  onClick={() => {
                    close();
                    setEnrich(true);
                  }}
                />
              )}
            </OverflowMenu>
          </EmailActions>
        }
      >
        {loadingEmails && (
          <EmailActions role="status" aria-live="polite" aria-busy="true">
            <Skeleton variant={SkeletonVariant.RECT} />
            <Text size={TextSize.BODY_SM}>Loading emails…</Text>
          </EmailActions>
        )}
        {activeRuns.isPending && !workflowId && (
          <Text size={TextSize.BODY_SM}>Checking enrichment status…</Text>
        )}
        {visibleRuns.map((active) => (
          <RunProgress key={active.workflowId} role="status" aria-live="polite">
            <Widget variant={WidgetVariant.SECONDARY}>
              <EmailHeader>
                <EmailActions>
                  <Status
                    value={
                      active.status === "completed" && active.emailFound === false
                        ? "partial"
                        : active.status
                    }
                    label={
                      active.status === "completed"
                        ? active.emailFound === false
                          ? "No email found"
                          : active.emailFound === true
                            ? "Email found"
                            : "Completed"
                        : undefined
                    }
                  />
                  <Text size={TextSize.BODY_SM}>{active.name}</Text>
                </EmailActions>
                <EmailActions>
                  {active.closedAt && (
                    <Text size={TextSize.BODY_SM}>
                      {new Date(active.closedAt).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Text>
                  )}
                  <Button
                    label="View run"
                    variant={ButtonVariant.TERTIARY}
                    onClick={() =>
                      router.push(
                        `/workflows?view=history&runId=${encodeURIComponent(active.workflowId)}`,
                      )
                    }
                    size={ButtonSize.SMALL}
                  />
                </EmailActions>
              </EmailHeader>
            </Widget>
          </RunProgress>
        ))}
        {emails.data?.primaryEmail || emails.data?.additionalEmails.length ? (
          <EmailList aria-label="Email addresses">
            {addresses.map((value, index) => (
              <EmailLine
                key={value}
                data-drop={
                  dropTarget?.email === value ? (dropTarget.after ? "after" : "before") : undefined
                }
                onDragOver={(event) => {
                  if (!dragged || busy) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  const rect = event.currentTarget.getBoundingClientRect();
                  setDropTarget({
                    email: value,
                    after: event.clientY > rect.top + rect.height / 2,
                  });
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragged && dropTarget && !busy) {
                    const remaining = addresses.filter((address) => address !== dragged);
                    const targetIndex = remaining.indexOf(value);
                    if (targetIndex >= 0) move(dragged, targetIndex + (dropTarget.after ? 1 : 0));
                  }
                  setDragged(null);
                  setDropTarget(null);
                }}
              >
                <Tooltip body={`Reorder ${value}`}>
                  <DragHandle
                    type="button"
                    draggable={!busy}
                    disabled={busy}
                    aria-label={`Reorder ${value}`}
                    title="Drag to reorder; first email is primary"
                    onKeyDown={(event) => {
                      if (event.key === "ArrowUp" && index > 0) {
                        event.preventDefault();
                        move(value, index - 1);
                      }
                      if (event.key === "ArrowDown" && index < addresses.length - 1) {
                        event.preventDefault();
                        move(value, index + 1);
                      }
                    }}
                    onDragStart={(event) => {
                      event.dataTransfer.setData("text/plain", value);
                      event.dataTransfer.effectAllowed = "move";
                      setDragged(value);
                    }}
                    onDragEnd={() => {
                      setDragged(null);
                      setDropTarget(null);
                    }}
                  >
                    <DotsSixVerticalIcon size={16} />
                  </DragHandle>
                </Tooltip>
                <div data-email-value>
                  <CopyInput
                    ariaLabel={`Email ${value}`}
                    value={value}
                    variant={InputVariant.BASE}
                    fillWidth
                  />
                </div>
                {value === emails.data?.primaryEmail && (
                  <Chip label="Primary" size={ChipSize.SMALL} />
                )}
                {(addresses.length > 1 || value !== emails.data?.primaryEmail) && (
                  <OverflowMenu
                    label={`Email options for ${value}`}
                    variant={ButtonVariant.TERTIARY}
                  >
                    {(close) => (
                      <>
                        {value !== emails.data?.primaryEmail && (
                          <MenuItem
                            icon={CheckIcon}
                            label="Make primary"
                            onClick={() => {
                              close();
                              if (!busy) move(value, 0);
                            }}
                          />
                        )}
                        {index > 0 && (
                          <MenuItem
                            icon={ArrowUpIcon}
                            label="Move up"
                            onClick={() => {
                              close();
                              if (!busy) move(value, index - 1);
                            }}
                          />
                        )}
                        {index < addresses.length - 1 && (
                          <MenuItem
                            icon={ArrowDownIcon}
                            label="Move down"
                            onClick={() => {
                              close();
                              if (!busy) move(value, index + 1);
                            }}
                          />
                        )}
                      </>
                    )}
                  </OverflowMenu>
                )}
                <Button
                  ariaLabel={`Remove ${value}`}
                  icon={TrashIcon}
                  variant={ButtonVariant.TERTIARY}
                  isDisabled={busy}
                  onClick={() => setRemovingEmail(value)}
                  tooltip={`Remove ${value}`}
                />
              </EmailLine>
            ))}
          </EmailList>
        ) : !emails.isPending && !running && !emails.error ? (
          <EmptyState
            header="No emails yet"
            description="Add an email or run enrichment to find one."
          />
        ) : null}
        {adding && (
          <EmailEditor
            onSubmit={(event) => {
              event.preventDefault();
              if (email && !busy) save.mutate({ value: email, primary: false });
            }}
          >
            <Input
              aria-label="New email"
              type="email"
              autoFocus
              value={email}
              placeholder="name@example.com"
              onChange={(event) => setEmail(event)}
              label="New email"
            />
            <Button
              ariaLabel="Save email"
              icon={CheckIcon}
              isDisabled={!email || busy}
              onClick={() => {
                if (email && !busy) save.mutate({ value: email, primary: false });
              }}
              tooltip="Save email"
            />
            <Button
              ariaLabel="Cancel adding email"
              icon={XIcon}
              variant={ButtonVariant.TERTIARY}
              onClick={() => setAdding(false)}
              tooltip="Cancel adding email"
            />
          </EmailEditor>
        )}
        {emails.error && (
          <Alert variant={AlertVariant.ERROR}>
            {<>Could not refresh emails. Use Refresh to try again.</>}
          </Alert>
        )}
        {(save.error || editList.error) && (
          <Alert variant={AlertVariant.ERROR}>{(save.error || editList.error)?.message}</Alert>
        )}
      </Widget>
      {
        <PersonEnrichmentDialog
          open={enrich}
          username={username}
          personId={id}
          onClose={() => setEnrich(false)}
          onStarted={setWorkflowId}
        />
      }
    </>
  );
}
